import type { RestApiPriceValueDto } from '@order-mage/integration-sdk';
import { roundTip } from '@/shared/lib/priceRound';
import { Country } from '@/shared/model/Country';
import { Currency } from '@/shared/model/Currency';

/**
 * A payment method's fee is either a FIXED amount in currency or a PERCENTAGE of
 * the order. Both arrive in `taxedPrice.priceGroup`; only `type` says
 * how to read `priceValues[].amount`.
 */
export type FeePriceGroup = {
    type?: string | null;
    priceValues?: Array<{ amount: string; currency: string; countryCode?: string | null }> | null;
};

export type FeeTaxedPrice = { priceGroup?: FeePriceGroup | null } | null;

/**
 * A resolved fee always carries the amount to charge; `percentage` is set only for
 * a PERCENTAGE fee, so the UI can tell the customer what the amount was derived
 * from ("25 Kč (2,5 %)").
 */
export type ResolvedFeePrice = RestApiPriceValueDto & { percentage?: number };

const PERCENTAGE = 'PERCENTAGE';

// TODO: Change this later when Kristian changes the BE — a percentage is stored
// under CZK only, whatever currency the venue actually sells in, so the lookup
// must ignore the selected currency instead of matching it.
const PERCENTAGE_SOURCE_CURRENCY = 'CZK';

type ResolveArgs = {
    taxedPrice: FeeTaxedPrice | undefined;
    /**
     * Order subtotal the percentage applies to: items (incl. addons) + packages.
     * Delivery, tips, the min-order top-up and the fee itself are excluded.
     */
    base: number;
    selectedCurrency: Currency;
    selectedCountry: Country;
};

/**
 * Resolve a payment method's fee into an amount the customer can be shown and
 * charged. Returns `null` when the method has no fee in the selected currency.
 */
export const resolvePaymentMethodFeePrice = ({
    taxedPrice,
    base,
    selectedCurrency,
    selectedCountry,
}: ResolveArgs): ResolvedFeePrice | null => {
    const priceValues = taxedPrice?.priceGroup?.priceValues;
    if (!priceValues?.length) return null;

    if (taxedPrice?.priceGroup?.type === PERCENTAGE) {
        const percentageValue = priceValues.find((pv) => pv.currency === PERCENTAGE_SOURCE_CURRENCY);
        const percentage = parseFloat(percentageValue?.amount ?? '');
        if (!Number.isFinite(percentage)) return null;
        // Rounded by the country's cash rule like tips (PRICE-2): the fee reaches the
        // POS as its own order line, and a fractional one breaks it (OMP-2166).
        const amount = roundTip((base * percentage) / 100, selectedCurrency, selectedCountry);
        return { amount: `${amount}`, currency: selectedCurrency, percentage };
    }

    const priceValue =
        priceValues.find((pv) => pv.countryCode === selectedCountry && pv.currency === selectedCurrency) ??
        priceValues.find((pv) => !pv.countryCode && pv.currency === selectedCurrency);
    return (priceValue as ResolvedFeePrice | undefined) ?? null;
};
